import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { Session } from '../services/session.service';
import { AuthService } from '../services/auth.service';

/**
 * Restricts routes to Weave internal users (sysAdmin).
 * Denies by returning a UrlTree to /home.
 *
 * Angular runs a route's guards concurrently, so on a fresh tab (no local
 * session yet) this guard runs while AuthGuard is still loading the session.
 * Reading `session.getUser()` synchronously would see no user and bounce a
 * real sysAdmin to /home. So when there is no user yet we wait for the same
 * auth signal AuthGuard drives (`AuthService.getAuthObservable()` emits true
 * once the session is created) instead of issuing a second session request.
 * If AuthGuard fails the navigation (redirect to /login), the router cancels
 * it and unsubscribes, so this wait never hangs.
 *
 * The role flag is `isSysAdmin` — the real `roles` wire shape (see
 * `userRoles` in session.service.ts). The previous `sysAdmin`/`pcAdmin`
 * fields never existed on the response, so this guard denied everyone.
 */
export const WeaveGuard: CanActivateFn = () => {
  const session = inject(Session);
  const authService = inject(AuthService);
  const router = inject(Router);

  const decide = () =>
    session.getUser()?.userRoles?.isSysAdmin ? true : router.createUrlTree(['/home']);

  if (session.getUser()) {
    return of(decide());
  }

  const sessionLoaded$: Observable<boolean> = authService.getAuthObservable().pipe(
    // The subject can hold a stale `true` while storage is empty (destroy()
    // doesn't reset it, logout flips it async, another tab can clear storage),
    // so also require an actual user before resolving.
    filter((loggedIn) => loggedIn && !!session.getUser()),
    first(),
  );
  return sessionLoaded$.pipe(map(decide));
};
